import { Skeleton } from 'antd'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { GetTeamStatOverviewRes } from '@/api/type-response'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import { Loading, Trans } from '@/components'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatGB2Bytes, formatPrice } from '@/utils/formatter'

import { StatsCard } from './components/StatsCard'
import { StorageProgressBar } from './components/StorageProgressBar'
import { TeamInfoHeader } from './components/TeamInfoHeader'
import { TeamResourcesTable } from './components/TeamResourcesTable'

export function AdminHomeView() {
  const [loading, setLoading] = useState(true)
  const [teamStatOverview, setTeamStatOverview] = useState<GetTeamStatOverviewRes | null>(null)
  const teamStore = useTeamStore()
  const account = useUserStore((state) => state.account)

  useEffect(() => {
    fetchTeamInfo()
  }, [])

  const fetchTeamInfo = async () => {
    try {
      const teamResID = teamStore.teamTokenData?.teamResID
      if (!teamResID) {
        console.error('No team res ID found')
        return
      }

      const statOverviewRes = await API.getTeamStatOverview({ res_id: teamResID })
      setTeamStatOverview(statOverviewRes)
    } catch (error) {
      console.error('Failed to fetch team data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Stats data from API
  const instancesUsage = teamStatOverview?.memberInstanceOverview.usage ?? 0
  const instancesQuota = teamStatOverview?.memberInstanceOverview.quota ?? 0
  const snapshotsUsed = teamStatOverview?.memberSnapshotOverview.usage ?? 0
  const snapshotsQuota = teamStatOverview?.memberSnapshotOverview.quota ?? 0

  // Team shared space from API (convert bytes to GB)
  const fsUsageGB =
    (teamStatOverview?.teamSharedFSSpaceOverview.usageBytes ?? 0) / formatGB2Bytes(1)
  const fsQuotaGB =
    (teamStatOverview?.teamSharedFSSpaceOverview.quotaBytes ?? 0) / formatGB2Bytes(1)

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto flex flex-col gap-2.5">
      {/* Team Info Header */}
      <TeamInfoHeader />

      <div className="bg-panel-white rounded-[32px] p-6 space-y-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <StatsCard
            title={<Trans i18nKey="teamHome.admin.stats.instances">Instances</Trans>}
            value={loading ? <Skeleton.Input active size="small" /> : instancesUsage}
            subValue={loading ? <Skeleton.Input active size="small" /> : instancesQuota}
            label={<Trans i18nKey="teamHome.admin.stats.instancesOnline">Online</Trans>}
            subLabel={<Trans i18nKey="teamHome.admin.stats.all">All</Trans>}
          />
          <StatsCard
            title={<Trans i18nKey="teamHome.admin.stats.snapshots">Snapshots</Trans>}
            value={loading ? <Skeleton.Input active size="small" /> : snapshotsUsed}
            subValue={loading ? <Skeleton.Input active size="small" /> : snapshotsQuota}
            label={<Trans i18nKey="teamHome.admin.stats.used">Used</Trans>}
            subLabel={<Trans i18nKey="teamHome.admin.stats.all">All</Trans>}
          />
          <StatsCard
            title={<Trans i18nKey="teamHome.admin.stats.totalSpending">Total Spending</Trans>}
            value={formatPrice(account.expense.amount ?? 0, false, '')}
            label={<Trans i18nKey="teamHome.admin.stats.credit">Credit</Trans>}
          />
        </div>

        {/* Team Space Usage Overview */}
        <div className="flex flex-col gap-4 border border-neutral-200 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="h6 text-primary">
              <Trans i18nKey="teamHome.admin.spaceOverview.title">Team Space Usage Overview</Trans>
            </h2>
            <Link
              to="/team/space"
              className="flex items-center t-sm text-secondary bg-panel-gray-100 pl-3 pr-2 py-2 rounded-md"
            >
              <Trans i18nKey="teamHome.admin.spaceOverview.storageSpaceLink">Storage Space</Trans>
              <ArrowRightIcon className="w-4 h-4 text-tertiary" />
            </Link>
          </div>
          <StorageProgressBar
            label={<Trans i18nKey="teamHome.admin.spaceOverview.datadrive">Datadrive</Trans>}
            usedGB={fsUsageGB}
            totalGB={fsQuotaGB}
            color="bg-btn-tertiary"
          />
        </div>
        <TeamResourcesTable />
      </div>
    </div>
  )
}
